import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Brain,
  Sparkles,
  Clock,
  Database,
  RefreshCw,
  ShieldCheck,
  BookOpen,
  ChevronDown,
  AlertTriangle,
  Trash2,
  X,
  Shield,
  DollarSign,
  Globe,
  Tag,
  Monitor,
  Terminal,
  Copy,
  Check,
  ExternalLink,
  Loader2,
  ChevronRight,
  ArrowLeft,
} from "lucide-react";
import { toast } from "sonner";
import { SecureMarkdown } from "@/components/SecureMarkdown";
import remarkGfm from "remark-gfm";
import SubscriptionFilter from "@/components/SubscriptionFilter";

const mdClasses = `prose prose-invert prose-sm max-w-none p-3
  [&_h1]:text-base [&_h1]:font-heading [&_h1]:text-[var(--hig-accent)] [&_h1]:mt-3 [&_h1]:mb-2
  [&_h2]:text-sm [&_h2]:font-heading [&_h2]:text-[var(--hig-accent)] [&_h2]:mt-4 [&_h2]:mb-2 [&_h2]:pb-1 [&_h2]:border-b [&_h2]:border-[var(--n-border-2)]
  [&_h3]:text-sm [&_h3]:font-heading [&_h3]:text-[var(--hig-accent)] [&_h3]:mt-3 [&_h3]:mb-1
  [&_p]:text-sm [&_p]:text-foreground/85 [&_p]:leading-relaxed
  [&_ul]:text-sm [&_ul]:text-foreground/80 [&_ol]:text-sm [&_ol]:text-foreground/80
  [&_li]:mb-1 [&_strong]:text-foreground
  [&_code]:text-[var(--hig-accent)] [&_code]:bg-[var(--hig-accent)]/10 [&_code]:px-1.5 [&_code]:py-0.5 [&_code]:rounded [&_code]:text-xs
  [&_hr]:border-[var(--n-border-2)] [&_hr]:my-3
  [&_table]:border-collapse [&_table]:w-full [&_table]:text-xs
  [&_th]:text-left [&_th]:px-3 [&_th]:py-2 [&_th]:border [&_th]:border-[var(--n-border-2)] [&_th]:bg-[var(--n-bg)] [&_th]:text-[var(--hig-accent)] [&_th]:font-medium
  [&_td]:px-3 [&_td]:py-2 [&_td]:border [&_td]:border-[var(--n-border-2)] [&_td]:text-foreground/80
  [&_tr:hover_td]:bg-[var(--n-hover)]`;

const SEVERITY_CONFIG = {
  critical: { color: "text-red-600 dark:text-red-400", bg: "bg-red-500/10", border: "border-red-500/20", label: "Critical" },
  high: { color: "text-amber-600 dark:text-amber-400", bg: "bg-amber-500/10", border: "border-amber-500/20", label: "High" },
  medium: { color: "text-yellow-400", bg: "bg-yellow-500/10", border: "border-yellow-500/20", label: "Medium" },
  low: { color: "text-blue-600 dark:text-blue-400", bg: "bg-blue-500/10", border: "border-blue-500/20", label: "Low" },
};

const CATEGORY_ICONS = {
  "GDPR & Data Residency": Globe,
  "Cost Governance": DollarSign,
  "Security & Network": Shield,
  "Tagging & Organization": Tag,
  "Compute & Storage": Monitor,
  "Monitoring & Compliance": ShieldCheck,
};

function InsightSection({ title, icon: Icon, iconColor, content, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  if (!content) return null;
  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger className="flex items-center justify-between w-full p-3 rounded-lg bg-[var(--n-bg)] border border-[var(--n-border-2)] hover:border-[#2a2a30] transition-colors">
        <div className="flex items-center gap-2">
          <Icon className={`w-4 h-4 ${iconColor}`} />
          <span className="text-sm font-medium">{title}</span>
        </div>
        <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </CollapsibleTrigger>
      <CollapsibleContent className="mt-2">
        <div className="pl-2 border-l-2 border-[var(--n-border-2)] ml-2">
          <SecureMarkdown content={content} className={mdClasses} />
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}

function parseInsightSections(analysis) {
  if (!analysis) return null;
  const sections = [];
  const lines = analysis.split("\n");
  let currentSection = null;
  let currentContent = [];
  for (const line of lines) {
    const h1Match = line.match(/^#\s+(.+)/);
    const h2Match = line.match(/^##\s+(.+)/);
    if (h1Match || h2Match) {
      if (currentSection) {
        sections.push({ title: currentSection, content: currentContent.join("\n").trim() });
      }
      currentSection = (h1Match || h2Match)[1].trim();
      currentContent = [];
    } else {
      currentContent.push(line);
    }
  }
  if (currentSection) {
    sections.push({ title: currentSection, content: currentContent.join("\n").trim() });
  }
  return sections.length > 0 ? sections : null;
}

function getSectionIcon(title) {
  const t = title.toLowerCase();
  if (t.includes("subscription") || t.includes("finding")) return { icon: Database, color: "text-[var(--hig-accent)]" };
  if (t.includes("governance")) return { icon: BookOpen, color: "text-violet-600 dark:text-violet-400" };
  if (t.includes("security")) return { icon: ShieldCheck, color: "text-amber-600 dark:text-amber-400" };
  if (t.includes("recommended") || t.includes("action")) return { icon: AlertTriangle, color: "text-red-600 dark:text-red-400" };
  return { icon: Brain, color: "text-[var(--hig-accent)]" };
}

function ViolationCard({ violation, onRemediate }) {
  const [expanded, setExpanded] = useState(false);
  const sev = SEVERITY_CONFIG[violation.severity] || SEVERITY_CONFIG.medium;
  const CatIcon = CATEGORY_ICONS[violation.category] || Shield;

  return (
    <div
      className={`rounded-lg border ${sev.border} ${sev.bg} p-3 space-y-2 transition-all`}
      data-testid={`violation-${violation.id}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-start gap-2 min-w-0">
          <CatIcon className={`w-4 h-4 ${sev.color} shrink-0 mt-0.5`} />
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-medium">{violation.policy_name}</span>
              <Badge variant="outline" className={`text-[10px] ${sev.border} ${sev.color}`}>
                {sev.label}
              </Badge>
              <Badge variant="outline" className="text-[10px] border-[var(--n-border-2)] text-muted-foreground">
                {violation.category}
              </Badge>
            </div>
            <p className="text-xs text-foreground/70 mt-1">{violation.description}</p>
          </div>
        </div>
      </div>

      <button
        onClick={() => setExpanded(!expanded)}
        className="text-[10px] text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1"
      >
        <ChevronDown className={`w-3 h-3 transition-transform ${expanded ? "rotate-180" : ""}`} />
        {expanded ? "Hide details" : "Show details"}
      </button>

      {expanded && (
        <div className="space-y-2 pt-1 animate-fade-in">
          <div>
            <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">Impact</p>
            <p className="text-xs text-foreground/80">{violation.impact}</p>
          </div>
          <div>
            <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">Affected Resources ({violation.affected_resources?.length || 0})</p>
            <div className="space-y-1 max-h-32 overflow-y-auto">
              {(violation.affected_resources || []).map((r, i) => (
                <div key={i} className="flex items-center gap-2 text-xs p-1.5 rounded bg-[var(--n-bg)]/50">
                  <span className="text-foreground font-medium truncate">{r.name}</span>
                  <span className="text-muted-foreground font-mono text-[10px] shrink-0">{r.type?.split("/").pop()}</span>
                  <span className="text-muted-foreground text-[10px] shrink-0 ml-auto">{r.resource_group}</span>
                </div>
              ))}
            </div>
          </div>
          <div>
            <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">Remediation</p>
            <p className="text-xs text-foreground/80">{violation.remediation_summary}</p>
          </div>
          {violation.is_actionable && (
            <div className="flex gap-2 pt-1">
              <Button
                size="sm"
                variant="outline"
                onClick={() => onRemediate(violation.id, "az_cli")}
                className={`text-xs ${sev.border} ${sev.color} hover:${sev.bg}`}
                data-testid={`remediate-azcli-${violation.id}`}
              >
                <Terminal className="w-3 h-3 mr-1" /> Azure CLI Script
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => onRemediate(violation.id, "powershell")}
                className={`text-xs ${sev.border} ${sev.color} hover:${sev.bg}`}
                data-testid={`remediate-ps-${violation.id}`}
              >
                <Terminal className="w-3 h-3 mr-1" /> PowerShell Script
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function RemediationPanel({ script, onBack }) {
  const [copied, setCopied] = useState(false);
  const copyScript = () => {
    navigator.clipboard.writeText(script.script);
    setCopied(true);
    toast.success("Script copied!");
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="space-y-4 animate-fade-in">
      <Button variant="ghost" size="sm" onClick={onBack} className="text-muted-foreground" data-testid="back-from-remediation">
        <ArrowLeft className="w-4 h-4 mr-1" /> Back to violations
      </Button>

      <Card className="bg-[var(--n-card)] border-[var(--n-border-2)]">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Terminal className="w-4 h-4 text-[var(--hig-accent)]" />
              Remediation: {script.violation?.policy_name}
            </CardTitle>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={copyScript} className="border-[var(--n-border-2)] text-xs" data-testid="copy-remediation-btn">
                {copied ? <Check className="w-3.5 h-3.5 mr-1 text-emerald-600 dark:text-emerald-400" /> : <Copy className="w-3.5 h-3.5 mr-1" />}
                {copied ? "Copied!" : "Copy"}
              </Button>
              <Button
                size="sm"
                onClick={() => window.open(script.cloud_shell_url, "_blank")}
                className="bg-[var(--hig-accent)] hover:opacity-90 text-primary-foreground font-semibold text-xs"
                data-testid="open-cloud-shell-remediation"
              >
                <ExternalLink className="w-3.5 h-3.5 mr-1" /> Azure Cloud Shell
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="bg-amber-500/5 border border-amber-500/20 rounded-lg px-3 py-2 text-xs text-amber-600 dark:text-amber-400 mb-4 flex items-start gap-2">
            <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
            <span>DRY_RUN is enabled by default. Review the script carefully and disable DRY_RUN only when ready to apply changes.</span>
          </div>
          <pre
            className="bg-[var(--n-bg)] border border-[var(--n-border-2)] rounded-lg p-4 text-xs font-mono text-foreground/90 overflow-x-auto max-h-[50vh] whitespace-pre-wrap"
            data-testid="remediation-script-content"
          >
            {script.script}
          </pre>
        </CardContent>
      </Card>

      <div className="flex items-center gap-3 text-xs text-muted-foreground">
        <span>1. Copy the script</span>
        <ChevronRight className="w-3 h-3" />
        <span>2. Open Azure Cloud Shell</span>
        <ChevronRight className="w-3 h-3" />
        <span>3. Paste and review</span>
        <ChevronRight className="w-3 h-3" />
        <span>4. Set DRY_RUN=false to apply</span>
      </div>
    </div>
  );
}

export default function InsightsPage() {
  const { api } = useAuth();
  const [activeTab, setActiveTab] = useState("reports");
  const [insights, setInsights] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [subscriptionId, setSubscriptionId] = useState("");

  // Policy state
  const [policyScans, setPolicyScans] = useState([]);
  const [policyLoading, setPolicyLoading] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [remediationScript, setRemediationScript] = useState(null);
  const [generatingRemediation, setGeneratingRemediation] = useState(null);

  const fetchInsights = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: "20" });
      if (subscriptionId) params.append("subscription_id", subscriptionId);
      const res = await api().get(`/insights?${params}`);
      setInsights(res.data);
    } catch (err) {
      console.error("Failed to fetch insights:", err);
    } finally {
      setLoading(false);
    }
  }, [api, subscriptionId]);

  const fetchPolicyScans = useCallback(async () => {
    setPolicyLoading(true);
    try {
      const params = new URLSearchParams({ limit: "10" });
      if (subscriptionId) params.append("subscription_id", subscriptionId);
      const res = await api().get(`/insights/policy-scans?${params}`);
      setPolicyScans(res.data);
    } catch (err) {
      console.error("Failed to fetch policy scans:", err);
    } finally {
      setPolicyLoading(false);
    }
  }, [api, subscriptionId]);

  useEffect(() => {
    fetchInsights();
    fetchPolicyScans();
  }, [fetchInsights, fetchPolicyScans]);

  const generateInsight = async () => {
    setGenerating(true);
    try {
      const body = {};
      if (subscriptionId) body.subscription_id = subscriptionId;
      const res = await api().post("/insights/generate", body);
      toast.success("Report generated!");
      setInsights((prev) => [res.data, ...prev]);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to generate");
    } finally {
      setGenerating(false);
    }
  };

  const deleteInsight = async (id) => {
    try {
      await api().delete(`/insights/${id}`);
      setInsights((prev) => prev.filter((i) => i.id !== id));
      toast.success("Report deleted");
    } catch (err) {
      toast.error("Failed to delete");
    }
  };

  const clearAll = async () => {
    try {
      const res = await api().delete("/insights");
      setInsights([]);
      toast.success(`Cleared ${res.data.deleted_count} reports`);
    } catch (err) {
      toast.error("Failed to clear");
    }
  };

  const runPolicyScan = async () => {
    setScanning(true);
    try {
      const body = {};
      if (subscriptionId) body.subscription_id = subscriptionId;
      const res = await api().post("/insights/policy-scan", body);
      toast.success(`Policy scan complete — ${res.data.total_violations} violations found`);
      setPolicyScans((prev) => [res.data, ...prev]);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Policy scan failed");
    } finally {
      setScanning(false);
    }
  };

  const deletePolicyScan = async (id) => {
    try {
      await api().delete(`/insights/policy-scans/${id}`);
      setPolicyScans((prev) => prev.filter((s) => s.id !== id));
      toast.success("Scan deleted");
    } catch (err) {
      toast.error("Failed to delete");
    }
  };

  const clearPolicyScans = async () => {
    try {
      const res = await api().delete("/insights/policy-scans");
      setPolicyScans([]);
      toast.success(`Cleared ${res.data.deleted_count} scans`);
    } catch (err) {
      toast.error("Failed to clear");
    }
  };

  const handleRemediate = async (violationId, scriptType) => {
    setGeneratingRemediation(violationId);
    try {
      const res = await api().post("/insights/policy-remediation", {
        violation_id: violationId,
        script_type: scriptType,
      });
      setRemediationScript(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to generate remediation script");
    } finally {
      setGeneratingRemediation(null);
    }
  };

  // If showing remediation script, render that panel
  if (remediationScript) {
    return (
      <div className="space-y-6" data-testid="insights-page">
        <RemediationPanel script={remediationScript} onBack={() => setRemediationScript(null)} />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="insights-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight font-heading">
            AI Insights
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Governance, security, and policy compliance analysis
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <SubscriptionFilter value={subscriptionId} onChange={setSubscriptionId} />
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="bg-[var(--n-card)] border border-[var(--n-border-2)] h-9">
          <TabsTrigger
            value="reports"
            className="text-xs data-[state=active]:bg-[var(--hig-accent)]/10 data-[state=active]:text-[var(--hig-accent)]"
            data-testid="tab-reports"
          >
            <Brain className="w-3.5 h-3.5 mr-1.5" /> Governance Reports
          </TabsTrigger>
          <TabsTrigger
            value="policy"
            className="text-xs data-[state=active]:bg-[var(--hig-accent)]/10 data-[state=active]:text-[var(--hig-accent)]"
            data-testid="tab-policy"
          >
            <Shield className="w-3.5 h-3.5 mr-1.5" /> Azure Policy
          </TabsTrigger>
        </TabsList>

        {/* === Governance Reports Tab === */}
        <TabsContent value="reports" className="space-y-4 mt-0">
          <div className="flex items-center justify-end gap-2">
            {insights.length > 1 && (
              <Button
                variant="outline"
                size="sm"
                onClick={clearAll}
                className="border-red-500/20 text-red-600 dark:text-red-400 hover:bg-red-500/10 text-xs"
                data-testid="clear-all-insights-btn"
              >
                <Trash2 className="w-3.5 h-3.5 mr-1" /> Clear All
              </Button>
            )}
            <Button
              onClick={generateInsight}
              disabled={generating}
              data-testid="generate-insight-button"
              className="bg-[var(--hig-accent)] hover:opacity-90 text-primary-foreground font-semibold"
            >
              {generating ? (
                <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Analyzing...</>
              ) : (
                <><Sparkles className="w-4 h-4 mr-2" /> Generate Report</>
              )}
            </Button>
          </div>

          {loading ? (
            <div className="space-y-4">
              {[...Array(2)].map((_, i) => (
                <Card key={i} className="bg-[var(--n-card)] border-[var(--n-border-2)]">
                  <CardContent className="p-6"><div className="h-32 bg-[var(--n-border-2)] rounded animate-pulse" /></CardContent>
                </Card>
              ))}
            </div>
          ) : insights.length === 0 ? (
            <Card className="bg-[var(--n-card)] border-[var(--n-border-2)]">
              <CardContent className="py-16 px-8 text-center">
                <div className="mx-auto w-16 h-16 rounded-2xl bg-[var(--hig-accent)]/10 flex items-center justify-center mb-6">
                  <Brain className="w-8 h-8 text-[var(--hig-accent)]" />
                </div>
                <h3 className="text-base font-semibold font-heading mb-2">No governance reports yet</h3>
                <p className="text-sm text-muted-foreground max-w-sm mx-auto mb-6 leading-relaxed">
                  Generate an AI-powered report to analyze your Azure resources, detect orphans, and get security & governance recommendations.
                </p>
                <Button
                  onClick={generateInsight}
                  disabled={generating}
                  className="bg-[var(--hig-accent)] hover:opacity-90 text-primary-foreground font-semibold"
                  data-testid="generate-first-insight-button"
                >
                  <Sparkles className="w-4 h-4 mr-2" /> Generate First Report
                </Button>
                <p className="text-[11px] text-muted-foreground mt-4">Takes about 30 seconds</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-6">
              {insights.map((insight, index) => {
                const sections = parseInsightSections(insight.analysis);
                return (
                  <Card
                    key={insight.id}
                    className="bg-[var(--n-card)] border-[var(--n-border-2)] animate-fade-in"
                    style={{ animationDelay: `${index * 0.05}s` }}
                    data-testid={`insight-card-${insight.id}`}
                  >
                    <CardHeader className="pb-3">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex items-center gap-2 min-w-0">
                          <Brain className="w-4 h-4 text-[var(--hig-accent)] shrink-0" />
                          <CardTitle className="text-sm font-medium truncate">
                            Governance & Security Report
                          </CardTitle>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <span className="text-[10px] text-muted-foreground flex items-center gap-1">
                            <Database className="w-3 h-3" /> {insight.resource_count}
                          </span>
                          <span className="text-[10px] text-muted-foreground flex items-center gap-1">
                            <Clock className="w-3 h-3" /> {new Date(insight.generated_at).toLocaleString()}
                          </span>
                          <button
                            onClick={() => deleteInsight(insight.id)}
                            className="p-1 text-muted-foreground hover:text-red-600 dark:text-red-400 transition-colors rounded"
                            data-testid={`delete-insight-${insight.id}`}
                            title="Delete this report"
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      {sections ? (
                        sections.map((section, si) => {
                          const { icon, color } = getSectionIcon(section.title);
                          return (
                            <InsightSection
                              key={si}
                              title={section.title}
                              icon={icon}
                              iconColor={color}
                              content={section.content}
                              defaultOpen={index === 0 && si === 0}
                            />
                          );
                        })
                      ) : (
                        <SecureMarkdown content={insight.analysis} className={mdClasses} />
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* === Azure Policy Tab === */}
        <TabsContent value="policy" className="space-y-4 mt-0">
          <div className="flex items-center justify-end gap-2">
            {policyScans.length > 1 && (
              <Button
                variant="outline"
                size="sm"
                onClick={clearPolicyScans}
                className="border-red-500/20 text-red-600 dark:text-red-400 hover:bg-red-500/10 text-xs"
                data-testid="clear-policy-scans-btn"
              >
                <Trash2 className="w-3.5 h-3.5 mr-1" /> Clear All
              </Button>
            )}
            <Button
              onClick={runPolicyScan}
              disabled={scanning}
              data-testid="run-policy-scan-btn"
              className="bg-[var(--hig-accent)] hover:opacity-90 text-primary-foreground font-semibold"
            >
              {scanning ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Scanning...</>
              ) : (
                <><Shield className="w-4 h-4 mr-2" /> Run Policy Scan</>
              )}
            </Button>
          </div>

          {policyLoading ? (
            <div className="space-y-4">
              {[...Array(2)].map((_, i) => (
                <Card key={i} className="bg-[var(--n-card)] border-[var(--n-border-2)]">
                  <CardContent className="p-6"><div className="h-32 bg-[var(--n-border-2)] rounded animate-pulse" /></CardContent>
                </Card>
              ))}
            </div>
          ) : policyScans.length === 0 ? (
            <Card className="bg-[var(--n-card)] border-[var(--n-border-2)]">
              <CardContent className="py-16 px-8 text-center">
                <div className="mx-auto w-16 h-16 rounded-2xl bg-[var(--hig-accent)]/10 flex items-center justify-center mb-6">
                  <Shield className="w-8 h-8 text-[var(--hig-accent)]" />
                </div>
                <h3 className="text-base font-semibold font-heading mb-2">No policy scans yet</h3>
                <p className="text-sm text-muted-foreground max-w-sm mx-auto mb-6 leading-relaxed">
                  Run a policy scan to check your Azure resources against GDPR, cost governance, security, and compliance policies.
                </p>
                <Button
                  onClick={runPolicyScan}
                  disabled={scanning}
                  className="bg-[var(--hig-accent)] hover:opacity-90 text-primary-foreground font-semibold"
                  data-testid="run-first-policy-scan-btn"
                >
                  <Shield className="w-4 h-4 mr-2" /> Run First Policy Scan
                </Button>
                <p className="text-[11px] text-muted-foreground mt-4">Takes about 15 seconds</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-6">
              {policyScans.map((scan, scanIdx) => (
                <Card
                  key={scan.id}
                  className="bg-[var(--n-card)] border-[var(--n-border-2)] animate-fade-in"
                  style={{ animationDelay: `${scanIdx * 0.05}s` }}
                  data-testid={`policy-scan-${scan.id}`}
                >
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-center gap-2 min-w-0">
                        <Shield className="w-4 h-4 text-[var(--hig-accent)] shrink-0" />
                        <CardTitle className="text-sm font-medium">
                          Policy Scan — {scan.total_violations} violation{scan.total_violations !== 1 ? "s" : ""}
                        </CardTitle>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <span className="text-[10px] text-muted-foreground flex items-center gap-1">
                          <Database className="w-3 h-3" /> {scan.resource_count}
                        </span>
                        <span className="text-[10px] text-muted-foreground flex items-center gap-1">
                          <Clock className="w-3 h-3" /> {new Date(scan.generated_at).toLocaleString()}
                        </span>
                        <button
                          onClick={() => deletePolicyScan(scan.id)}
                          className="p-1 text-muted-foreground hover:text-red-600 dark:text-red-400 transition-colors rounded"
                          data-testid={`delete-policy-scan-${scan.id}`}
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {/* Severity summary */}
                    <div className="flex gap-2 flex-wrap">
                      {Object.entries(scan.by_severity || {}).map(([sev, count]) => {
                        const cfg = SEVERITY_CONFIG[sev] || SEVERITY_CONFIG.medium;
                        return (
                          <Badge key={sev} variant="outline" className={`text-[10px] ${cfg.border} ${cfg.color}`}>
                            {cfg.label}: {count}
                          </Badge>
                        );
                      })}
                      {Object.entries(scan.by_category || {}).map(([cat, count]) => (
                        <Badge key={cat} variant="outline" className="text-[10px] border-[var(--n-border-2)] text-muted-foreground">
                          {cat}: {count}
                        </Badge>
                      ))}
                    </div>

                    {/* Violations grouped by severity */}
                    <div className="space-y-2">
                      {(scan.violations || [])
                        .sort((a, b) => {
                          const order = { critical: 0, high: 1, medium: 2, low: 3 };
                          return (order[a.severity] ?? 2) - (order[b.severity] ?? 2);
                        })
                        .map((v) => (
                          <ViolationCard
                            key={v.id}
                            violation={v}
                            onRemediate={(vid, st) => {
                              if (generatingRemediation === vid) return;
                              handleRemediate(vid, st);
                            }}
                          />
                        ))}
                    </div>

                    {generatingRemediation && (
                      <div className="flex items-center gap-2 text-xs text-[var(--hig-accent)] py-2">
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        Generating remediation script...
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
